import Phaser from 'phaser'
import { ServerConnection } from '../network/ServerConnection'
import { SoundManager }     from '../sound/SoundManager'
import {
  ServerMessage, StateMessage, PlayerDTO, Team, RoomMode, SpeedKey,
} from '@shared/protocol'
import {
  BOARD_COLS, BOARD_ROWS, CELL_EMPTY,
  BLOCK_COLOR, BLOCK_DARK,
  DAS_DELAY, DAS_REPEAT,
  SUB_OFFSETS,
} from '@shared/constants'

// ─── Static layout constants ──────────────────────────────────────────────────
const LOCAL_BS    = 44
const LOCAL_X     = 56
const LOCAL_Y     = 52
const OPP_X       = 420
const OPP_PANEL_Y = 52

const TEAM_COLOR: Record<Team, number> = { A: 0x3366ff, B: 0xff3344 }

// ─── Dynamic opponent layout ──────────────────────────────────────────────────
interface OppLayout { bs: number; cols: number; gap: number }

function calcOppLayout(count: number): OppLayout {
  if (count === 0) return { bs: 30, cols: 1, gap: 10 }
  const availH = 720 - OPP_PANEL_Y - 10
  const availW = 1280 - OPP_X - 10
  const gap    = count <= 3 ? 12 : 6

  // Single column
  const bsS = Math.floor((availH - Math.max(0, count - 1) * gap) / (count * BOARD_ROWS))
  if (bsS >= 8) return { bs: Math.min(bsS, 30), cols: 1, gap }

  // Two columns
  const nRows = Math.ceil(count / 2)
  const bsH   = Math.floor((availH - Math.max(0, nRows - 1) * gap) / (nRows * BOARD_ROWS))
  const bsW   = Math.floor((availW - gap) / (2 * BOARD_COLS))
  return { bs: Math.max(5, Math.min(bsH, bsW, 30)), cols: 2, gap }
}

export interface MultiplayerSceneData {
  playerId:    string
  displayName: string
  roomId:      string
  mode:        RoomMode
  speed:       SpeedKey
  team:        Team
}

interface PlayerTexts {
  name:  Phaser.GameObjects.Text
  score: Phaser.GameObjects.Text
  chain: Phaser.GameObjects.Text
}

export class MultiplayerScene extends Phaser.Scene {
  private cfg!:        MultiplayerSceneData
  private conn!:       ServerConnection
  private latestState: StateMessage | null = null
  private localId      = ''
  private sound        = new SoundManager()

  private gfx!:          Phaser.GameObjects.Graphics
  private txtStatus!:    Phaser.GameObjects.Text
  private txtCountdown!: Phaser.GameObjects.Text
  private txtGameOver!:  Phaser.GameObjects.Text

  // DAS
  private keyLeft!:  Phaser.Input.Keyboard.Key
  private keyRight!: Phaser.Input.Keyboard.Key
  private dasTimer   = 0
  private dasRepeat  = 0
  private dasActive  = false

  // Local prediction (left/right/rotate applied immediately)
  private localPiece = { col: 2, row: 1, rot: 0, valid: false }

  // Sound/shake tracking
  private prevLocalPhase        = ''
  private prevIncomingGarbage   = 0

  // Per-player animated overlays
  private playerTexts = new Map<string, PlayerTexts>()
  private chainAnims  = new Map<string, { count: number; expiresAt: number }>()

  // Explosion animation state
  private savedFlashCellCount = 0
  private pendingExplosions: Array<{ x: number; y: number; color: number }> = []

  constructor() { super({ key: 'MultiplayerScene' }) }

  init(data: MultiplayerSceneData): void { this.cfg = data }

  create(): void {
    this.gfx = this.add.graphics()

    this.txtStatus = this.add.text(640, 20, 'Connecting…', {
      fontFamily: 'monospace', fontSize: '14px', color: '#888899',
    }).setOrigin(0.5, 0)

    this.txtCountdown = this.add.text(640, 280, '', {
      fontFamily: 'monospace', fontSize: '120px', color: '#FFE566',
      stroke: '#000', strokeThickness: 8,
    }).setOrigin(0.5).setDepth(10)

    this.txtGameOver = this.add.text(640, 300, '', {
      fontFamily: 'monospace', fontSize: '52px', color: '#FF6B6B',
      stroke: '#000', strokeThickness: 6, align: 'center',
    }).setOrigin(0.5).setDepth(10).setVisible(false)

    // ── Keyboard ──────────────────────────────────────────────────────────────
    this.keyLeft  = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT)
    this.keyRight = this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT)

    this.input.keyboard!.on('keydown-UP',    () => { this.conn.sendInput('rotate_cw');  this.predictRotate(1)  })
    this.input.keyboard!.on('keydown-X',     () => { this.conn.sendInput('rotate_cw');  this.predictRotate(1)  })
    this.input.keyboard!.on('keydown-Z',     () => { this.conn.sendInput('rotate_ccw'); this.predictRotate(-1) })
    this.input.keyboard!.on('keydown-SPACE', () => this.conn.sendInput('hard_drop'))
    this.input.keyboard!.on('keydown-DOWN',  () => this.conn.sendInput('soft_drop_start'))
    this.input.keyboard!.on('keyup-DOWN',    () => this.conn.sendInput('soft_drop_end'))
    this.input.keyboard!.on('keydown-S',     () => this.conn.sendInput('soft_drop_start'))
    this.input.keyboard!.on('keyup-S',       () => this.conn.sendInput('soft_drop_end'))

    this.input.keyboard!.on('keydown-LEFT', () => {
      this.conn.sendInput('move_left')
      this.predictMove(-1)
      this.dasTimer = 0; this.dasActive = false
    })
    this.input.keyboard!.on('keydown-RIGHT', () => {
      this.conn.sendInput('move_right')
      this.predictMove(1)
      this.dasTimer = 0; this.dasActive = false
    })
    this.input.keyboard!.on('keyup-LEFT',  () => { this.dasTimer = 0; this.dasActive = false })
    this.input.keyboard!.on('keyup-RIGHT', () => { this.dasTimer = 0; this.dasActive = false })
    this.input.keyboard!.on('keydown-ESC', () => this.leaveGame())

    // Desbloqueia o AudioContext na primeira interação (política de autoplay do browser)
    this.input.keyboard!.once('keydown',  () => this.sound.unlock())
    this.input.once('pointerdown',         () => this.sound.unlock())

    // ── Touch controls (mobile) ────────────────────────────────────────────────
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      this.createTouchControls()
    }

    // ── Connect ───────────────────────────────────────────────────────────────
    this.conn = new ServerConnection(
      msg  => this.handleServerMessage(msg),
      stat => this.txtStatus.setText(stat.toUpperCase()),
    )
    this.conn.connect({
      roomId:      this.cfg.roomId,
      playerId:    this.cfg.playerId,
      displayName: this.cfg.displayName,
      team:        this.cfg.team,
      mode:        this.cfg.mode,
      speed:       this.cfg.speed,
    })

    this.events.on('shutdown', () => {
      this.conn.disconnect()
      this.playerTexts.forEach(t => { t.name.destroy(); t.score.destroy(); t.chain.destroy() })
      this.playerTexts.clear()
      this.chainAnims.clear()
    })
  }

  // ─── Local prediction helpers ────────────────────────────────────────────────
  private predictMove(dir: -1 | 1): void {
    if (!this.localPiece.valid) return
    this.localPiece.col = Math.max(0, Math.min(BOARD_COLS - 1, this.localPiece.col + dir))
  }

  private predictRotate(dir: 1 | -1): void {
    if (!this.localPiece.valid) return
    this.localPiece.rot = ((this.localPiece.rot + dir + 4) % 4)
  }

  // ─── Server message handler ──────────────────────────────────────────────────
  private handleServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'join_ack':
        this.localId  = msg.playerId
        this.cfg.team = msg.team
        this.txtStatus.setText(`Room: ${this.cfg.roomId.slice(0, 8)}… | Team ${msg.team}`)
        this.conn.sendReady()
        break

      case 'state': {
        this.latestState = msg
        // Reconcile local prediction with server state
        const me = msg.players.find(p => p.id === this.localId)
        if (me?.phaseKind === 'falling') {
          this.localPiece = { col: me.pieceCol, row: me.pieceRow, rot: me.pieceRot, valid: true }
        } else {
          this.localPiece.valid = false
        }
        break
      }

      case 'game_over': {
        const won = this.isLocalTeam(msg.winnerTeam)
        this.txtGameOver
          .setText(won ? 'YOU WIN!\n\nSpace to play again\nESC to quit' : 'YOU LOSE\n\nSpace to play again\nESC to quit')
          .setColor(won ? '#4ECB71' : '#FF6B6B')
          .setVisible(true)
        won ? this.sound.playWin() : this.sound.playLose()
        this.input.keyboard!.once('keydown-SPACE', () => this.restartGame())
        break
      }

      case 'chain_event':
        if (msg.chainCount >= 2) {
          this.chainAnims.set(msg.playerId, {
            count:     msg.chainCount,
            expiresAt: Date.now() + 2200,
          })
          // Play chain sound only for local player's chains
          if (msg.playerId === this.localId) this.sound.playChain(msg.chainCount)
        }
        break
    }
  }

  private isLocalTeam(team: Team): boolean {
    return this.latestState?.players.find(p => p.id === this.localId)?.team === team
  }

  // ─── Update ──────────────────────────────────────────────────────────────────
  update(_time: number, delta: number): void {
    const dt = delta / 1000
    this.handleDAS(dt)

    if (!this.latestState) return

    const { roomPhase, countdown, players } = this.latestState

    if (roomPhase === 'countdown') {
      this.txtCountdown.setText(countdown > 0 ? String(countdown) : 'GO!').setVisible(true)
    } else {
      this.txtCountdown.setVisible(false)
    }

    this.render(_time, players)
  }

  // ─── DAS ─────────────────────────────────────────────────────────────────────
  private handleDAS(dt: number): void {
    if (this.keyLeft.isDown || this.keyRight.isDown) {
      this.dasTimer += dt
      if (!this.dasActive && this.dasTimer >= DAS_DELAY) {
        this.dasActive = true; this.dasRepeat = 0
      }
      if (this.dasActive) {
        this.dasRepeat += dt
        if (this.dasRepeat >= DAS_REPEAT) {
          this.dasRepeat = 0
          if (this.keyLeft.isDown)  { this.conn.sendInput('move_left');  this.predictMove(-1) }
          if (this.keyRight.isDown) { this.conn.sendInput('move_right'); this.predictMove(1)  }
        }
      }
    } else {
      this.dasTimer = 0; this.dasActive = false
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────────
  private render(time: number, players: PlayerDTO[]): void {
    this.gfx.clear()

    const local  = players.find(p => p.id === this.localId)
    const others = players.filter(p => p.id !== this.localId)

    if (local) {
      // Sound/shake: detect changes on local player
      this.detectLocalEvents(local)
      this.renderLocalBoard(time, local)
    }

    others.forEach((p, i) => this.renderOpponentBoard(time, p, i, others.length))

    // Hide text overlays for disconnected players
    const activeIds = new Set(players.map(p => p.id))
    this.playerTexts.forEach((t, id) => {
      if (!activeIds.has(id)) {
        t.name.setVisible(false); t.score.setVisible(false); t.chain.setVisible(false)
      }
    })
  }

  // ─── Side-effect detection on local player state ────────────────────────────
  private detectLocalEvents(p: PlayerDTO): void {
    // Salva contagem de células enquanto estão piscando
    // (quando a fase muda para fora de chain_flash, flashingCells já está vazio)
    if (p.phaseKind === 'chain_flash') {
      this.savedFlashCellCount = Math.max(this.savedFlashCellCount, p.flashingCells.length)
    }

    // Transição chain_flash → outra fase: blocos explodiram
    if (this.prevLocalPhase === 'chain_flash' && p.phaseKind !== 'chain_flash') {
      this.sound.playPop(Math.max(1, Math.ceil(this.savedFlashCellCount / 4)))
      this.savedFlashCellCount = 0
      this.triggerExplosions()
    }
    this.prevLocalPhase = p.phaseKind

    // Garbage incoming increased → thud + camera shake
    if (p.incomingGarbage > this.prevIncomingGarbage) {
      const delta = p.incomingGarbage - this.prevIncomingGarbage
      this.sound.playGarbageReceived(delta)
      const intensity = Math.min(0.022, 0.004 + delta * 0.0012)
      this.cameras.main.shake(280, intensity)
    }
    this.prevIncomingGarbage = p.incomingGarbage
  }

  // ─── Per-player text objects (lazy) ─────────────────────────────────────────
  private getPlayerTexts(playerId: string): PlayerTexts {
    if (!this.playerTexts.has(playerId)) {
      this.playerTexts.set(playerId, {
        name: this.add.text(0, 0, '', {
          fontFamily: 'monospace', fontSize: '13px', color: '#ccccee',
          stroke: '#000', strokeThickness: 2,
        }).setOrigin(0.5, 1).setDepth(3),
        score: this.add.text(0, 0, '', {
          fontFamily: 'monospace', fontSize: '12px', color: '#8888aa',
        }).setDepth(3),
        chain: this.add.text(0, 0, '', {
          fontFamily: 'monospace', fontSize: '28px', color: '#FFE566',
          stroke: '#000', strokeThickness: 5,
        }).setOrigin(0.5).setDepth(6).setVisible(false),
      })
    }
    return this.playerTexts.get(playerId)!
  }

  // ─── Local board ─────────────────────────────────────────────────────────────
  private renderLocalBoard(time: number, p: PlayerDTO): void {
    const bs     = LOCAL_BS
    const bx     = LOCAL_X
    const by     = LOCAL_Y
    const boardW = BOARD_COLS * bs
    const boardH = BOARD_ROWS * bs

    this.gfx.fillStyle(0x12122a)
    this.gfx.fillRect(bx, by, boardW, boardH)
    this.gfx.lineStyle(1, 0x1e1e42, 0.6)
    for (let c = 1; c < BOARD_COLS; c++)
      this.gfx.lineBetween(bx + c * bs, by, bx + c * bs, by + boardH)
    for (let r = 1; r < BOARD_ROWS; r++)
      this.gfx.lineBetween(bx, by + r * bs, bx + boardW, by + r * bs)

    const flashing = new Set(p.flashingCells)
    const inFlash  = p.phaseKind === 'chain_flash'
    const flashOn  = inFlash ? (Math.floor(time / 80) % 2 === 0) : false

    for (let row = 0; row < BOARD_ROWS; row++) {
      for (let col = 0; col < BOARD_COLS; col++) {
        const val = p.board[row * BOARD_COLS + col]
        if (val === CELL_EMPTY) continue
        const key    = `${col},${row}`
        const isGlow = inFlash && flashing.has(key)
        const alpha  = isGlow && !flashOn ? 0.25 : 1
        const color  = isGlow && flashOn  ? 0xffffff : BLOCK_COLOR[val]
        const dark   = isGlow && flashOn  ? 0xcccccc : BLOCK_DARK[val]
        this.drawBlock(bx + col * bs, by + row * bs, bs, color, dark, alpha)
      }
    }

    // Armazena posições dos blocos piscando para a animação de explosão
    if (inFlash && p.flashingCells.length > 0) {
      this.pendingExplosions = p.flashingCells.map(key => {
        const [col, row] = key.split(',').map(Number)
        const val = p.board[row * BOARD_COLS + col]
        return { x: bx + col * bs + bs / 2, y: by + row * bs + bs / 2, color: BLOCK_COLOR[val] ?? 0xffffff }
      })
    }

    // Use local-predicted piece position
    const lCol = this.localPiece.valid ? this.localPiece.col : p.pieceCol
    const lRow = this.localPiece.valid ? this.localPiece.row : p.pieceRow
    const lRot = this.localPiece.valid ? this.localPiece.rot : p.pieceRot
    const [dc, dr] = SUB_OFFSETS[lRot]

    // Ghost (only when prediction matches server — avoids wrong ghost position)
    const predActive = this.localPiece.valid &&
      (this.localPiece.col !== p.pieceCol || this.localPiece.rot !== p.pieceRot)
    if (!predActive && p.phaseKind === 'falling' && p.ghostRow !== p.pieceRow) {
      this.drawBlockAlpha(bx + p.pieceCol * bs,        by + p.ghostRow * bs,        bs, BLOCK_COLOR[p.piece1], 0.2)
      this.drawBlockAlpha(bx + (p.pieceCol + dc) * bs, by + (p.ghostRow + dr) * bs, bs, BLOCK_COLOR[p.piece2], 0.2)
    }

    // Current piece (predicted)
    if (p.phaseKind === 'falling') {
      this.drawBlock(bx + lCol * bs,        by + lRow * bs,        bs, BLOCK_COLOR[p.piece1], BLOCK_DARK[p.piece1])
      this.drawBlock(bx + (lCol + dc) * bs, by + (lRow + dr) * bs, bs, BLOCK_COLOR[p.piece2], BLOCK_DARK[p.piece2])
    }

    // Team border
    this.gfx.lineStyle(3, TEAM_COLOR[p.team])
    this.gfx.strokeRect(bx - 1, by - 1, boardW + 2, boardH + 2)

    // Next piece preview
    this.renderNextPreview(bx + boardW + 12, by, bs, p.next1, p.next2)

    // Garbage bar (left)
    if (p.incomingGarbage > 0) {
      this.gfx.fillStyle(0xFF6B6B, 0.8)
      const barH = Math.min(p.incomingGarbage * 8, boardH)
      this.gfx.fillRect(bx - 12, by + boardH - barH, 8, barH)
    }

    // Garbage HUD above board
    this.renderGarbageHUD(bx, by - 24, boardW, p.incomingGarbage, 10)

    // Name + score text overlays
    const texts = this.getPlayerTexts(p.id)
    texts.name
      .setPosition(bx + boardW / 2, by - 2)
      .setText(p.displayName)
      .setFontSize(13).setOrigin(0.5, 1).setVisible(true)
    texts.score
      .setPosition(bx + boardW / 2, by + boardH + 6)
      .setText(`SCORE  ${p.score.toLocaleString()}`)
      .setFontSize(12).setOrigin(0.5, 0).setVisible(true)

    this.updateChainAnim(p.id, bx + boardW / 2, by + boardH / 2, 28)
  }

  // ─── Opponent boards ──────────────────────────────────────────────────────────
  private renderOpponentBoard(time: number, p: PlayerDTO, index: number, total: number): void {
    const layout = calcOppLayout(total)
    const { bs, cols, gap } = layout
    const bw = BOARD_COLS * bs
    const bh = BOARD_ROWS * bs

    const colIdx = cols === 1 ? 0 : index % cols
    const rowIdx = cols === 1 ? index : Math.floor(index / cols)
    const nRows  = cols === 1 ? total : Math.ceil(total / cols)

    const totalH = nRows * bh + (nRows - 1) * gap
    const startY = OPP_PANEL_Y + Math.max(0, (this.scale.height - totalH) / 2 - OPP_PANEL_Y)
    const bx     = OPP_X + colIdx * (bw + gap)
    const by     = startY + rowIdx * (bh + gap)

    const alpha = p.alive ? 1 : 0.3

    this.gfx.fillStyle(0x12122a, alpha)
    this.gfx.fillRect(bx, by, bw, bh)

    const flashing = new Set(p.flashingCells)
    const inFlash  = p.phaseKind === 'chain_flash'
    const flashOn  = inFlash ? (Math.floor(time / 80) % 2 === 0) : false

    for (let row = 0; row < BOARD_ROWS; row++) {
      for (let col = 0; col < BOARD_COLS; col++) {
        const val = p.board[row * BOARD_COLS + col]
        if (val === CELL_EMPTY) continue
        const key       = `${col},${row}`
        const isGlow    = inFlash && flashing.has(key)
        const cellAlpha = (isGlow && !flashOn ? 0.25 : 1) * alpha
        const color     = isGlow && flashOn ? 0xffffff : BLOCK_COLOR[val]
        const dark      = isGlow && flashOn ? 0xcccccc : BLOCK_DARK[val]
        this.drawBlock(bx + col * bs, by + row * bs, bs, color, dark, cellAlpha)
      }
    }

    if (p.phaseKind === 'falling' && p.alive) {
      const [dc, dr] = SUB_OFFSETS[p.pieceRot]
      this.drawBlock(bx + p.pieceCol * bs,        by + p.pieceRow * bs,        bs, BLOCK_COLOR[p.piece1], BLOCK_DARK[p.piece1], alpha)
      this.drawBlock(bx + (p.pieceCol + dc) * bs, by + (p.pieceRow + dr) * bs, bs, BLOCK_COLOR[p.piece2], BLOCK_DARK[p.piece2], alpha)
    }

    this.gfx.lineStyle(2, TEAM_COLOR[p.team], p.alive ? 1 : 0.3)
    this.gfx.strokeRect(bx - 1, by - 1, bw + 2, bh + 2)

    if (!p.alive) {
      this.gfx.fillStyle(0x000000, 0.5)
      this.gfx.fillRect(bx, by, bw, bh)
    }

    if (p.incomingGarbage > 0) {
      this.gfx.fillStyle(0xFF6B6B, 0.8)
      const barH = Math.min(p.incomingGarbage * 5, bh)
      this.gfx.fillRect(bx - 8, by + bh - barH, 5, barH)
    }

    // Name + score
    const texts  = this.getPlayerTexts(p.id)
    const nameY  = by > 16 ? by - 2 : by + Math.floor(bs * 1.2)
    texts.name
      .setPosition(bx + bw / 2, nameY)
      .setText(p.displayName)
      .setFontSize(Math.max(9, bs - 4)).setOrigin(0.5, 1).setVisible(true)
    texts.score
      .setPosition(bx + bw + 4, by + 2)
      .setText(p.score.toLocaleString())
      .setFontSize(Math.max(9, bs - 6)).setOrigin(0, 0).setVisible(true)

    this.updateChainAnim(p.id, bx + bw / 2, by + bh / 2, Math.max(14, bs * 0.8))
  }

  // ─── Garbage HUD ─────────────────────────────────────────────────────────────
  private renderGarbageHUD(bx: number, iconY: number, boardW: number, amount: number, maxSize: number): void {
    if (amount <= 0) return

    const large  = Math.floor(amount / 30)
    const medium = Math.floor((amount % 30) / 6)
    const small  = amount % 6

    const icons: Array<{ sz: number; color: number }> = []
    for (let i = 0; i < Math.min(large,  3); i++) icons.push({ sz: maxSize,                    color: 0xff3344 })
    for (let i = 0; i < Math.min(medium, 5); i++) icons.push({ sz: Math.floor(maxSize * 0.74), color: 0xff7733 })
    for (let i = 0; i < Math.min(small,  6); i++) icons.push({ sz: Math.floor(maxSize * 0.55), color: 0xffaa33 })
    if (icons.length === 0) return

    const gap    = 3
    const totalW = icons.reduce((s, ic) => s + ic.sz + gap, 0) - gap
    let   x      = bx + Math.max(0, Math.floor((boardW - totalW) / 2))

    for (const ic of icons) {
      const cy = iconY + Math.floor((maxSize - ic.sz) / 2)
      this.gfx.fillStyle(ic.color, 0.9)
      this.gfx.fillRoundedRect(x, cy, ic.sz, ic.sz, 2)
      this.gfx.lineStyle(1, 0xffffff, 0.25)
      this.gfx.strokeRoundedRect(x, cy, ic.sz, ic.sz, 2)
      x += ic.sz + gap
    }
  }

  // ─── Chain animation ──────────────────────────────────────────────────────────
  private updateChainAnim(playerId: string, cx: number, cy: number, fontSize: number): void {
    const texts = this.getPlayerTexts(playerId)
    const anim  = this.chainAnims.get(playerId)
    if (!anim || Date.now() > anim.expiresAt) { texts.chain.setVisible(false); return }

    const progress = (anim.expiresAt - Date.now()) / 2200
    const alpha    = Math.min(1, progress * 3)
    texts.chain
      .setPosition(cx, cy)
      .setText(`×${anim.count} CHAIN!`)
      .setFontSize(Math.round(fontSize))
      .setAlpha(alpha)
      .setVisible(true)
  }

  // ─── Next piece preview ───────────────────────────────────────────────────────
  private renderNextPreview(x: number, y: number, bs: number, color1: number, color2: number): void {
    const bw = bs * 2 + 16
    this.gfx.fillStyle(0x12122a)
    this.gfx.fillRect(x, y, bw, bw)
    this.gfx.lineStyle(1, 0x3a3a7a)
    this.gfx.strokeRect(x, y, bw, bw)
    const cx = x + Math.floor(bw / 2 - bs / 2)
    this.drawBlock(cx, y + 8,      bs, BLOCK_COLOR[color2], BLOCK_DARK[color2])
    this.drawBlock(cx, y + 8 + bs, bs, BLOCK_COLOR[color1], BLOCK_DARK[color1])
  }

  // ─── Mobile touch controls ───────────────────────────────────────────────────
  private createTouchControls(): void {
    const alpha = 0.45
    const W = this.scale.width
    const H = this.scale.height

    const makeBtn = (
      lx: number, ly: number, w: number, h: number,
      label: string, color: number,
      onDown: () => void, onUp?: () => void,
    ): void => {
      const rect = this.add.rectangle(lx, ly, w, h, color, alpha)
        .setOrigin(0.5).setDepth(20).setInteractive()
      this.add.text(lx, ly, label, {
        fontFamily: 'monospace', fontSize: '18px', color: '#ffffff',
      }).setOrigin(0.5).setDepth(21).setAlpha(alpha + 0.2)

      rect.on('pointerdown', onDown)
      if (onUp) rect.on('pointerup', onUp)
    }

    const btnW = 90
    const btnH = 60
    const baseY = H - 38

    // Left / Right / Down
    makeBtn(btnW / 2 + 10,        baseY, btnW, btnH, '◀', 0x334455,
      () => { this.conn.sendInput('move_left');  this.predictMove(-1) })
    makeBtn(btnW + btnW / 2 + 20, baseY, btnW, btnH, '▶', 0x334455,
      () => { this.conn.sendInput('move_right'); this.predictMove(1) })
    makeBtn(btnW + 20,            baseY + 56, btnW, btnH - 12, '▼', 0x334455,
      () => this.conn.sendInput('soft_drop_start'),
      () => this.conn.sendInput('soft_drop_end'))

    // Rotate CCW / CW
    makeBtn(W - btnW * 2 - 20, baseY, btnW, btnH, 'CCW', 0x443355,
      () => { this.conn.sendInput('rotate_ccw'); this.predictRotate(-1) })
    makeBtn(W - btnW / 2 - 10, baseY, btnW, btnH, 'CW', 0x443355,
      () => { this.conn.sendInput('rotate_cw'); this.predictRotate(1) })

    // Hard drop
    makeBtn(W - btnW - 10, baseY + 56, btnW * 1.2, btnH - 12, '⬇ DROP', 0x553333,
      () => this.conn.sendInput('hard_drop'))
  }

  // ─── Block drawing ────────────────────────────────────────────────────────────
  private drawBlock(x: number, y: number, bs: number, color: number, dark: number, alpha = 1): void {
    const p = 2
    this.gfx.fillStyle(color, alpha)
    this.gfx.fillRoundedRect(x + p, y + p, bs - p * 2, bs - p * 2, 4)
    this.gfx.fillStyle(0xffffff, 0.20 * alpha)
    this.gfx.fillRect(x + p + 2, y + p + 2, bs - p * 2 - 4, Math.floor((bs - p * 2) * 0.38))
    this.gfx.fillStyle(dark, 0.30 * alpha)
    this.gfx.fillRect(x + p + 2, y + bs - p - 5, bs - p * 2 - 4, 4)
  }

  private drawBlockAlpha(x: number, y: number, bs: number, color: number, alpha: number): void {
    this.gfx.fillStyle(color, alpha)
    this.gfx.fillRoundedRect(x + 2, y + 2, bs - 4, bs - 4, 4)
  }

  // ─── Explosion particles ──────────────────────────────────────────────────────
  private triggerExplosions(): void {
    for (const { x, y, color } of this.pendingExplosions) {
      for (let i = 0; i < 5; i++) {
        const angle = (Math.PI * 2 * i) / 5 + Math.random() * 0.5
        const dist  = 18 + Math.random() * 20
        const r     = 2.5 + Math.random() * 2
        const dot   = this.add.circle(x, y, r, color, 0.9).setDepth(4)
        this.tweens.add({
          targets:    dot,
          x:          x + Math.cos(angle) * dist,
          y:          y + Math.sin(angle) * dist,
          alpha:      0,
          scaleX:     0.15,
          scaleY:     0.15,
          duration:   280 + Math.random() * 140,
          ease:       'Quad.Out',
          onComplete: () => dot.destroy(),
        })
      }
    }
    this.pendingExplosions = []
  }

  // ─── Navigation ───────────────────────────────────────────────────────────────
  private restartGame(): void {
    this.txtGameOver.setVisible(false)
    this.conn.disconnect()
    this.scene.restart(this.cfg)
  }

  private leaveGame(): void {
    this.conn.disconnect()
    this.scene.start('LobbyScene')
  }
}
